import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { uniqBy } from 'lodash-es';
import { getAvatarColorById } from 'assets/colors';
import { AsyncSelect, Select, SelectOption, SelectProps, AsyncProps } from '@ets/design-system';
import importedUserSvg from 'assets/images/IMPORTED_USER.svg';
import removedUserAvatar from 'assets/images/REMOVED_USER.svg';

import { useDispatch, useSelector, usePublicProfiles } from '_common/hooks';
import { AuthService, GroupsService, Logger } from '_common/services';
import { getPublicGroup } from 'App/redux/publicSlice';
import { parseProfile } from 'Auth/redux/utils';

import { useOnboardingExplorerActive } from '_common/hooks/Onboarding';
import { GHOST_USERS } from '_common/services/api/consts';
import avatarApi, { useGetMultipleAvatarQuery } from 'Auth/redux/avatarApi';
import { MultiValue, OnChangeValue } from 'react-select';

//TODO: these extra props shouldnt really be optional, check a way to do this. If this is done change this Required<SearchUserOption> bellow
export type SearchUserOption = {
  id?: string;
  email?: string;
  username?: string;
};

export type UserOption = SelectOption &
  SearchUserOption & {
    name: string;
    is_superuser?: boolean;
    has_avatar?: boolean;
    type: 'user';
    imported?: boolean;
  };

export type SearchUserProps<
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
  IsCreatable extends boolean = false,
> = Omit<AsyncProps<Option, IsMulti, IsCreatable>, 'variant'> & {
  searchable?: boolean;
  groups?: boolean;
  valuesToFilter?: string[] | null;
  filterOption?: SelectProps<SelectOption>['filterOption'];
  noOptionsMessage?: string;
  escapeClearsValue?: boolean;
  both?: boolean;
  editor?: boolean;
  options?: ({ id: UserId; type?: string } | UserOption)[];
  loadOnRender?: string;
  onLoadOptions?: (options: Promise<UserOption[]>) => void;
};

export type GroupOption = Omit<SelectOption, 'icon'> & {
  type: 'group';
  icon: {
    icon: string;
    size: number;
  };
};

const SearchUser = <
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
  IsCreatable extends boolean = false,
>({
  valuesToFilter = null,
  filterOption,
  placeholder,
  options,
  value = null,
  onChange,
  isClearable = true,
  noOptionsMessage,
  disabled,
  escapeClearsValue = false,
  defaultOptions = [],
  menuPosition = 'absolute',
  searchable,
  onInputChange,
  inputValue,
  size,
  width,
  both,
  editor = false,
  hideSelectedOptions,
  hasError,
  loadOnRender,
  onLoadOptions,
  testId,
  onMenuClose,
  id,
  menuLabel,
  ...props
}: SearchUserProps<Option, IsMulti, IsCreatable>) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const publicGroups = useSelector((state) => state.public.groups.profiles);
  const onboardingExplorerIsActive = useOnboardingExplorerActive();
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const filteredUserOptions = useMemo(
    () => userOptions.filter((user) => user.type === 'user' && user.has_avatar),
    [userOptions],
  );
  const userIds = filteredUserOptions.map((user) => user.value);
  const { data: avatars } = useGetMultipleAvatarQuery(userIds);
  const extractValues = (v: SelectOption | MultiValue<Option> | null) => {
    if (Array.isArray(v)) return v.map((v) => v?.value);
    return v && 'value' in v && v?.value !== undefined ? [v.value] : [];
  };
  const values = extractValues(value);

  const { profiles } = usePublicProfiles(
    options
      ?.map((option) => ('id' in option ? option.id : 'value' in option ? option.value : null))
      .filter((id): id is string => typeof id === 'string'),
  );
  const { profiles: selectedUsers } = usePublicProfiles(values);

  const parsedOptions = useMemo(() => {
    if (options && options?.length > 0) {
      return options
        ?.filter(
          (option: { id: UserId; type?: string } | UserOption | GroupOption) =>
            ('type' in option && option.type !== 'user') ||
            (option?.id && (profiles[option.id] || ('imported' in option && option.imported))),
        )
        .map((option) => {
          const searchUserOption = option as Required<UserOption | GroupOption>;

          const handleAvatar = () => {
            if (
              'id' in searchUserOption &&
              searchUserOption.id &&
              (!('imported' in searchUserOption) || !searchUserOption.imported)
            ) {
              return searchUserOption.avatar || avatars?.[searchUserOption.id];
            }
            if ('imported' in searchUserOption && searchUserOption.imported) {
              return importedUserSvg;
            } else {
              return removedUserAvatar;
            }
          };

          if (searchUserOption.type === 'user') {
            const id = searchUserOption.id ?? '';
            return {
              ...searchUserOption,
              avatar: {
                imageSrc: handleAvatar(),
                color: getAvatarColorById(id || '', profiles[id]?.initials),
                children: profiles[id]?.initials,
                size: 'm',
              },
              value: searchUserOption.value || id,
              description:
                profiles[id]?.email || searchUserOption.email || searchUserOption.description,
              label:
                profiles[id]?.name || searchUserOption.username || searchUserOption.label || id,
              type: 'user',
            } as unknown as Option;
          } else {
            return {
              ...searchUserOption,
              icon:
                props.groups || searchUserOption.type === 'group'
                  ? { icon: 'users', size: 4 }
                  : undefined,
            } as unknown as Option;
          }
        });
    }
  }, [options, profiles]);

  const parsedValues = useMemo(() => {
    if (!value) {
      return value;
    }

    const parseValue = (searchUserOption: Required<UserOption> | Required<GroupOption>) => {
      let label = null;

      if (searchUserOption.type === 'user') {
        const selectedOption = options?.find(
          (option) =>
            ('id' in option && option.id === searchUserOption.value) ||
            ('value' in option && option.value === searchUserOption.value),
        );
        if ((selectedOption as unknown as UserOption)?.imported) {
          label = searchUserOption.value;
        } else {
          label = selectedUsers[searchUserOption.value]?.name || searchUserOption.label;
        }
      } else if (searchUserOption.type === 'group') {
        label = searchUserOption.label;
      } else {
        label = '';
      }

      return {
        ...searchUserOption,
        avatar: searchUserOption.avatar,
        description:
          profiles[searchUserOption.value]?.email ||
          (searchUserOption as UserOption).email ||
          searchUserOption.description,
        label,
      } as unknown as Option;
    };

    if (value) {
      if (!Array.isArray(value)) {
        return parseValue(value as unknown as Required<UserOption>);
      } else if (value.length > 0) {
        return value.map(parseValue);
      } else {
        return value;
      }
    }
  }, [value, profiles, selectedUsers]);

  useEffect(() => {
    if (loadOnRender) {
      loadOptions?.(loadOnRender);
    }
  }, []);

  const getUserOption = (user: UserPublicProfile & { avatar?: string }): UserOption => {
    const parsedProfile = parseProfile(user);
    const color = getAvatarColorById(user.id, parsedProfile.initials);
    const avatarSrc = avatars?.[parsedProfile.id];
    return {
      name: parsedProfile.name,
      username: user.username,
      is_superuser: user.is_superuser,
      value: user.id,
      label: parsedProfile.name,
      description: user.email,
      has_avatar: user.has_avatar,
      avatar: {
        imageSrc: user.avatar || avatarSrc,
        color,
        children: parsedProfile.initials,
        size: 'm',
        testId: `search-user-avatar-${parsedProfile.id}`,
      },
      type: 'user',
    };
  };

  const getGroupOption = (group: Group): GroupOption => {
    return {
      icon: { icon: 'users', size: 4 },
      value: group.id,
      label: group.name,
      description: intl.formatMessage({ id: 'NUMBER_OF_USERS' }, { number: group.users.length }),
      type: 'group',
    };
  };

  const getUserAutocomplete = (input: string) => {
    return new Promise<UserOption[]>((resolve, reject) => {
      if (onboardingExplorerIsActive) {
        const davidBeanProfile = parseProfile(GHOST_USERS.davidBean);
        const { email, first_name, last_name, username, name } = davidBeanProfile;
        const query = input.toLowerCase();

        if (
          email.toLowerCase().includes(query) ||
          name.toLowerCase().includes(query) ||
          first_name.toLowerCase().includes(query) ||
          last_name.toLowerCase().includes(query) ||
          username.toLowerCase().includes(query)
        ) {
          resolve([getUserOption(GHOST_USERS.davidBean)]);
        } else {
          resolve([]);
        }
      } else {
        new AuthService()
          .getUserAutocomplete(input)
          .then((response) => {
            const optionsPromise = response.data.items.map(async (user) => {
              let userOption: UserPublicProfile & { avatar?: string };
              if (user?.has_avatar) {
                const { data } = await dispatch(
                  avatarApi.endpoints.getMultipleAvatar.initiate([user.id]),
                );
                userOption = {
                  ...user,
                  avatar: data?.[user.id],
                };
              } else {
                userOption = user;
              }
              return getUserOption(userOption);
            });
            Promise.all(optionsPromise).then((options) => resolve(options));
          })
          .catch((error) => {
            Logger.captureMessage('Error in group autocomplete', {
              extra: error,
            });
            reject(error);
          });
      }
    });
  };

  const getGroupAutocomplete = (input: string) => {
    return new Promise<GroupOption[]>((resolve, reject) => {
      new GroupsService()
        .getGroupAutocomplete(input)
        .then((response) => {
          const options = response.data.items.map((group) => {
            if (!publicGroups[group.id]) {
              dispatch(getPublicGroup(group.id));
            }
            return getGroupOption(group);
          });
          resolve(options);
        })
        .catch((error) => {
          Logger.captureException('Error in user autocomplete', {
            extra: error,
          });
          reject(error);
        });
    });
  };

  const getBothAutocomplete = (input: string) => {
    return new Promise<UserOption[]>(async (resolve, reject) => {
      const values = onboardingExplorerIsActive
        ? await Promise.all([getUserAutocomplete(input), []])
        : await Promise.all([getUserAutocomplete(input), getGroupAutocomplete(input)]);
      if (value && Array.isArray(value)) {
        resolve(uniqBy([...value, ...values[0], ...values[1]], (v) => v.value));
      } else {
        //@ts-expect-error getBothAutocomplete should return Promise<(UserOption | GroupOption)[]>
        resolve([...values[0], ...values[1]]);
      }
    });
  };

  const loadOptions = (input?: string) => {
    if (!input) {
      return;
    }

    let loadPromise: Promise<UserOption[]>;

    if (both) {
      loadPromise = getBothAutocomplete(input);
    } else if (props.groups) {
      //@ts-expect-error loadPromise should be typed to allow both User and Group
      loadPromise = getGroupAutocomplete(input);
    } else {
      loadPromise = getUserAutocomplete(input);
    }

    loadPromise
      .then((users) => {
        setUserOptions(users);
        onLoadOptions?.(Promise.resolve(users));
      })
      .catch((error) => {
        Logger.captureMessage('Error in loading options', {
          extra: error,
        });
      });

    return loadPromise;
  };

  const handleOnChange: SelectProps<Option, IsMulti>['onChange'] = (value, actionMeta) => {
    if (onChange) {
      if (!value) {
        onChange(value, actionMeta);
      } else if (value && Array.isArray(value)) {
        onChange(
          value.map((itValue: Option) => ({
            ...itValue,
            label: profiles[itValue.value]?.name ?? itValue.label,
          })) as unknown as OnChangeValue<Option, IsMulti>,
          actionMeta,
        );
      } else {
        if ('value' in value) {
          onChange({ ...value, label: profiles[value.value]?.name ?? value.label }, actionMeta);
        }
      }
    }
  };

  const handleFilterOption: SelectProps<Option>['filterOption'] = (option, inputValue) => {
    if (filterOption) {
      return filterOption(option, inputValue);
    }
    const optionMatchesInputValue = (option.data as unknown as UserOption).name
      ? (option.data as unknown as UserOption).name
          .toLowerCase()
          .includes(inputValue.toLowerCase()) ||
        (typeof option?.data?.description === 'string' &&
          option.data.description.toLowerCase().includes(inputValue.toLowerCase()))
      : (typeof option.label === 'string' &&
          option.label.toLowerCase().includes(inputValue.toLowerCase())) ||
        (typeof option?.data?.description === 'string' &&
          option.data.description.toLowerCase().includes(inputValue.toLowerCase()));
    if (valuesToFilter && valuesToFilter.length) {
      return !valuesToFilter.includes(option.value) && optionMatchesInputValue;
    }

    return optionMatchesInputValue;
  };

  if (options) {
    return (
      <Select
        variant="standard"
        id={id}
        size={size ?? 'm'}
        isSearchable={searchable}
        isClearable={isClearable}
        inputValue={inputValue}
        onInputChange={onInputChange}
        placeholder={placeholder}
        isCreatable={props.isCreatable}
        options={parsedOptions}
        value={
          Array.isArray(parsedValues)
            ? parsedValues
            : (parsedOptions?.find(
                (option) => option.value === (parsedValues as Option | undefined)?.value,
              ) ?? parsedValues)
        }
        onChange={handleOnChange}
        filterOption={handleFilterOption}
        noOptionsMessage={noOptionsMessage}
        width={width}
        isMulti={props.isMulti}
        unselectedGroupLabel={intl.formatMessage({
          id: 'UNSELECTED_USERS',
        })}
        overflowLabel={(selected: number) =>
          `${selected} ${intl.formatMessage({
            id: 'USERS_SELECTED',
          })}
                `
        }
        selectedGroupLabel={intl.formatMessage({
          id: 'SELECTED_USERS',
        })}
        closeMenuOnSelect={!props.isMulti}
        hasError={hasError}
        onOptionCreate={() => intl.formatMessage({ id: 'NO_RESULTS_FOUND' })}
        testId={`${testId}-search-user-select`}
        disabled={disabled}
        onMenuClose={onMenuClose}
      />
    );
  }

  return (
    <AsyncSelect
      variant="standard"
      isCreatable={props.isCreatable}
      isClearable={isClearable}
      loadOptions={loadOptions as AsyncProps<Option>['loadOptions']}
      hideSelectedOptions={hideSelectedOptions === undefined ? !props.isMulti : hideSelectedOptions}
      escapeClearsValue={escapeClearsValue}
      filterOption={handleFilterOption}
      defaultOptions={defaultOptions}
      onChange={handleOnChange}
      value={parsedValues}
      placeholder={placeholder || intl.formatMessage({ id: 'global.typeToSearch' })}
      noOptionsMessage={({ inputValue }: { inputValue: string }) => {
        if (inputValue === '') {
          return intl.formatMessage({
            id: 'START_TYPING_TO_SEE_THE_LIST_OF_USERS_OR_GROUPS',
          });
        } else return intl.formatMessage({ id: 'global.noResults' });
      }}
      overflowLabel={
        props.isMulti
          ? (selected: number) =>
              `${selected} ${intl.formatMessage({
                id: 'USERS_SELECTED',
              })}
                `
          : undefined
      }
      menuPosition={menuPosition}
      size={size ?? 'm'}
      disabled={disabled}
      width={width}
      isMulti={props.isMulti}
      groupOptionsBySelection={props.isMulti}
      selectedGroupLabel={(selected: number) => {
        return `${intl
          .formatMessage({
            id: 'SELECTED_USERS',
          })
          .toUpperCase()} (${selected})`;
      }}
      unselectedGroupLabel={intl.formatMessage({ id: 'UNSELECTED_USERS' }).toUpperCase()}
      hasError={hasError}
      onMenuClose={onMenuClose}
      testId={`${testId}-search-user`}
      menuLabel={menuLabel}
    />
  );
};

export default SearchUser;
